import React from "react";
import HeaderLogo from "../../assets/images/header-logo.png";

const PrivacyPolicy = () => {

  return (
    <div className="min-h-screen bg-white">
      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="text-sm text-gray-600 mb-8">
            <p>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Aao Ghumen ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal information. This privacy policy explains our minimal data collection practices and how we handle information when you use our services. We believe in privacy by design and collect only what is absolutely necessary for service delivery.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Our Data Minimization Approach</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>We do not store personal data.</strong> Our approach is built on privacy and data minimization:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Mobile Number:</strong> Used only for verification purposes during booking. Not stored permanently in our systems.</li>
                <li><strong>Trip Preferences:</strong> Collected temporarily for trip planning and personalization during your session only.</li>
                <li><strong>Technical Data:</strong> Basic browser information for functionality (not stored permanently).</li>
                <li><strong>Session Data:</strong> Temporary data to maintain your session while using our services.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Important:</strong> All data collected is processed in real-time for service delivery and is not stored in permanent databases.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Handle Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We process information temporarily and only for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Mobile number verification for booking confirmation (discarded after verification)</li>
                <li>Trip planning and route optimization during your session</li>
                <li>Personalization of recommendations based on your current preferences</li>
                <li>Processing payments through secure third-party processors</li>
                <li>Providing customer support during your trip</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>No permanent storage:</strong> Once your trip is completed and payment is processed, all personal information is automatically purged from our systems.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Sharing and Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Since we don't store personal data, our sharing is extremely limited and only occurs during active service delivery:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Payment Processors:</strong> Your payment information is processed directly by secure third-party payment gateways (we never see or store payment details)</li>
                <li><strong>SMS Verification:</strong> Mobile numbers are shared with SMS service providers only for verification (immediately discarded)</li>
                <li><strong>Tour Partners:</strong> Basic trip details shared with local tour operators during active bookings only</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>No permanent sharing:</strong> We do not sell, rent, or permanently share any personal information with third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security and Protection</h2>
              <p className="text-gray-700 leading-relaxed">
                Since we process data in real-time without permanent storage, our security approach focuses on:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-4">
                <li>Encrypted connections (HTTPS) for all data transmission</li>
                <li>Secure, temporary processing environments</li>
                <li>Automatic data purging after session completion</li>
                <li>No permanent databases containing personal information</li>
                <li>Limited access to processing systems</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention (Minimal Approach)</h2>
              <p className="text-gray-700 leading-relaxed">
                <strong>We practice automatic data deletion:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-4">
                <li>Mobile numbers: Deleted immediately after verification</li>
                <li>Trip preferences: Cleared at the end of your session</li>
                <li>Payment information: Never stored (handled by payment processors)</li>
                <li>Session data: Automatically expires within 24 hours</li>
                <li>Communication logs: Kept only during active trip for support purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Control</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Since we don't store personal data permanently, your privacy rights are naturally protected:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Right to Access:</strong> Since data isn't stored, there's no permanent data to access</li>
                <li><strong>Right to Correction:</strong> You can update preferences during your active session</li>
                <li><strong>Right to Erasure:</strong> Data is automatically erased after processing</li>
                <li><strong>Right to Object:</strong> You can stop using our services at any time</li>
                <li><strong>Right to Portability:</strong> Not applicable as we don't store personal data</li>
                <li><strong>Session Control:</strong> You can clear your session data by closing your browser</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed">
                We use minimal, essential cookies only for:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-4">
                <li>Maintaining your session while browsing</li>
                <li>Remembering your preferences during your visit</li>
                <li>Basic functionality and security</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>No tracking cookies:</strong> We don't use analytics, advertising, or tracking cookies. All cookies are session-based and expire when you close your browser.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                We integrate with trusted third-party services for specific functions:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-4">
                <li><strong>Payment Gateways:</strong> Handle payments securely (we never see your payment details)</li>
                <li><strong>SMS Services:</strong> For mobile verification only</li>
                <li><strong>Maps Services:</strong> For route planning and navigation</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                These services have their own privacy policies. We don't control their data practices, but we choose services with strong privacy commitments.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Policy Updates</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this privacy policy to reflect changes in our minimal data practices or legal requirements. Any updates will be posted on this page with a revised "last updated" date. Since we don't store personal data, policy changes won't affect previously collected information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about our privacy-first approach or this policy, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700"><strong>Email:</strong> aaoghumen@gmail.com</p>
                <p className="text-gray-700"><strong>Phone:</strong> +91 97139 08893</p>
                <p className="text-gray-700 mt-2"><strong>Privacy Commitment:</strong> We're happy to explain our no-storage approach in detail.</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <img src={HeaderLogo} alt="Aao Ghumen" className="h-8 w-auto mx-auto mb-4 filter brightness-0 invert" />
            <p className="text-gray-400">
              &copy; 2025 Aao Ghumen. All rights reserved. Discover the world with us.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
